type ComposerDirtyState = {
    attributes: unknown;
    templates: unknown;
    areas: unknown;
    designs: Map<unknown, unknown>;
    productQuantity: unknown;
    ecommerceSettings: unknown;
};

const dirtySnapshotReplacer = (key: string, value: unknown) => {
    // Validation decorates nodes with fresh Maps. Those warnings are derived UI state and must
    // not turn a freshly loaded Composer into an edited one.
    if (key === 'brokenValues') return undefined;

    if (value instanceof Map) {
        return Array.from(value.entries()).sort(([left], [right]) => String(left).localeCompare(String(right)));
    }

    if (value && typeof value === 'object' && 'toJSON' in value && typeof (value as { toJSON?: unknown }).toJSON === 'function') {
        return (value as { toJSON: () => unknown }).toJSON();
    }

    return value;
};

/**
 * Captures only persisted Composer data. Selection, validation and other view-only state are
 * deliberately excluded so background hydration cannot create a false unsaved-change warning.
 */
export const createComposerDirtySnapshot = (state: ComposerDirtyState) =>
    JSON.stringify(
        {
            attributes: state.attributes,
            templates: state.templates,
            areas: state.areas,
            designs: state.designs,
            productQuantity: state.productQuantity,
            ecommerceSettings: state.ecommerceSettings
        },
        dirtySnapshotReplacer
    );

/** The first preset can be created before its empty container is ready to save. */
export const hasComposerChangesOutsidePreset = (currentSnapshot: string, savedSnapshot: string, presetID: string) => {
    const withoutPreset = (snapshot: string) => {
        const state = JSON.parse(snapshot);
        state.attributes = state.attributes.filter((node: { id: string }) => node.id !== presetID);
        return JSON.stringify(state);
    };
    return withoutPreset(currentSnapshot) !== withoutPreset(savedSnapshot);
};

/** Only the selected area's server-backed placement needs an early save before Full Editor. */
export const hasComposerAreaChangesRequiredByFullEditor = (currentSnapshot: string, savedSnapshot: string, areaId: string) => {
    const current = JSON.parse(currentSnapshot);
    const saved = JSON.parse(savedSnapshot);
    const currentArea = (current.areas as Array<Record<string, unknown>> | undefined)?.find((candidate) => candidate.id === areaId);
    if (!currentArea) return true;
    // Composer UI node GUIDs are regenerated while loading a product. A persisted sideID is the
    // stable identity across local-draft hydration and a fresh server snapshot.
    const savedArea = (saved.areas as Array<Record<string, unknown>> | undefined)?.find((candidate) =>
        Number(currentArea.sideID) > 0 ? candidate.sideID === currentArea.sideID : candidate.id === currentArea.id
    );
    const placement = (area?: Record<string, unknown>) => {
        if (!area) return null;
        const keys = [
            'sideID',
            'displayOrder',
            'name',
            'ppcm',
            'objects',
            'width',
            'height',
            'camera',
            'uvMap',
            'customTextureResolution',
            'desktopTextureResolution',
            'mobileTextureResolution'
        ];
        const attribute = Number(area.attributeID) > 0 ? `saved:${area.attributeID}` : `draft:${area.attributeGuid ?? ''}`;
        return [attribute, ...keys.map((key) => area[key] ?? null)];
    };
    return JSON.stringify(placement(currentArea)) !== JSON.stringify(placement(savedArea));
};
